import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getCategoryTree } from '../../services/categoryService.js';

export default function MegaMenu() {
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['categories', 'tree'],
    queryFn: () => getCategoryTree().then((res) => res.data || []),
    staleTime: 1000 * 60 * 5,
  });

  const tree = data || [];
  const activeTop = tree.find((c) => c.id === activeId) || tree[0] || null;

  const close = () => {
    setOpen(false);
    setActiveId(null);
  };

  return (
    <div
      className="relative"
      onMouseLeave={close}
    >
      <button
        type="button"
        onMouseEnter={() => setOpen(true)}
        onClick={() => setOpen((v) => !v)}
        className="whitespace-nowrap bg-brand-500 px-3 py-2.5 text-sm font-medium text-white sm:px-4 sm:py-3"
      >
        All Categories
      </button>

      {open && (
        <div
          className="absolute left-0 top-full z-30 flex w-[min(60rem,calc(100vw-1.5rem))] flex-col border bg-white text-slate-800 shadow-lg lg:flex-row"
          role="menu"
        >
          {/* Top-level rail */}
          <ul className="w-full shrink-0 border-b lg:w-60 lg:border-b-0 lg:border-r">
            {isLoading && (
              <li className="px-4 py-3 text-sm text-slate-400">Loading…</li>
            )}
            {!isLoading && tree.length === 0 && (
              <li className="px-4 py-3 text-sm text-slate-400">No categories yet.</li>
            )}
            {tree.map((c) => (
              <li
                key={c.id}
                onMouseEnter={() => setActiveId(c.id)}
              >
                <Link
                  to={`/category/${c.slug}`}
                  onClick={close}
                  className={`flex items-center justify-between px-4 py-2 text-sm hover:bg-slate-100 ${
                    activeTop?.id === c.id ? 'bg-slate-50 font-medium' : ''
                  }`}
                >
                  <span>{c.name}</span>
                  {c.children?.length > 0 && (
                    <span aria-hidden className="text-slate-400">›</span>
                  )}
                </Link>
              </li>
            ))}
          </ul>

          {/* Sub-tree panel (depth 2 + 3) */}
          {activeTop?.children?.length > 0 && (
            <div className="grid w-full gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {activeTop.children.map((sub) => (
                <div key={sub.id} className="min-w-0">
                  <Link
                    to={`/category/${sub.slug}`}
                    onClick={close}
                    className="block text-sm font-semibold text-slate-900 hover:text-brand-600"
                  >
                    {sub.name}
                  </Link>
                  {sub.children?.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {sub.children.map((leaf) => (
                        <li key={leaf.id}>
                          <Link
                            to={`/category/${leaf.slug}`}
                            onClick={close}
                            className="block text-sm text-slate-600 hover:text-brand-600"
                          >
                            {leaf.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
